import { APIError, isApiErrorResponse } from "@bvhi/backend-api/api-error";
import { Card, CardContent, CardHeader, Stack, Typography } from "@bvhi/ui-base";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { ReactElement } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { getAppConfig } from "../config/config";
import { DefaultForbiddenFallback } from "./default-forbidden-fallback";
import { DefaultNotFoundFallback, DefaultNotFoundFallbackProps } from "./default-not-found-fallback";

interface Props {
  Component: () => ReactElement;
  NotFoundFallback?: ({ resetErrorBoundary }: DefaultNotFoundFallbackProps) => JSX.Element;
  ForbiddenFallback?: ({ resetErrorBoundary }: DefaultNotFoundFallbackProps) => JSX.Element;
}

export const WithErrorBoundary = ({ Component: Component, NotFoundFallback, ForbiddenFallback }: Props) => {
  const { reset } = useQueryErrorResetBoundary();

  let isNotInProduction = false;
  if (getAppConfig().env.stage === "stage-dev") isNotInProduction = true;
  if (getAppConfig().env.stage === "stage-test") isNotInProduction = true;
  if (getAppConfig().env.stage === "local-dev") isNotInProduction = true;
  if (getAppConfig().env.stage === "local") isNotInProduction = true;

  return (
    <ErrorBoundary
      onReset={reset}
      fallbackRender={({ error, resetErrorBoundary }) => {
        if (isApiErrorResponse(error as APIError) && (error as APIError).status === 404 && NotFoundFallback) {
          return <NotFoundFallback resetErrorBoundary={resetErrorBoundary} />;
        }
        if (isApiErrorResponse(error as APIError) && (error as APIError).status === 404 && !NotFoundFallback) {
          return <DefaultNotFoundFallback resetErrorBoundary={resetErrorBoundary} />;
        }

        if (isApiErrorResponse(error as APIError) && (error as APIError).status === 403 && ForbiddenFallback) {
          return <ForbiddenFallback resetErrorBoundary={resetErrorBoundary} />;
        }
        if (isApiErrorResponse(error as APIError) && (error as APIError).status === 403 && !ForbiddenFallback) {
          return <DefaultForbiddenFallback resetErrorBoundary={resetErrorBoundary} />;
        }

        return (
          <Stack pt={2} spacing={2}>
            <Card>
              <CardHeader
                title="Oh nein! Es ist ein Fehler aufgetreten!"
                subheader={
                  <Typography>
                    Das tut uns Leid! Die Seite konnte leider nicht geladen werden. Sie können versuchen, die Seite neu zu laden. Wenn
                    dieses Problem weiterhin besteht, können Sie sich gerne per&nbsp;
                    <a href={`mailto:${getAppConfig().env.supportMailAdesso}`}>E-Mail</a>
                    &nbsp;an uns wenden.
                  </Typography>
                }
              />
            </Card>
            {isNotInProduction && (
              <Card>
                <CardContent>
                  {(error as Error).stack && (
                    <>
                      <Typography variant="h6">Error Stack:</Typography>
                      <pre>{(error as Error).stack}</pre>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </Stack>
        );
      }}
    >
      <Component />
    </ErrorBoundary>
  );
};
